'use client';

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { useDebounceValue } from 'usehooks-ts';
import { Box } from '@terra/ui-styled-system';
import { ListPageLayout } from '@terra/ui-design-system';
import { AlertCircleIcon, Database01Icon, Search01Icon } from '@terra/ui-icons';
import { AI_COMPONENT_TYPE_KEYS } from '@terra/ui-ai-applications';
import type {
  AiComponentDto,
  AiComponentListSegment,
} from '@terra/shared-contracts-ai-applications';
import {
  DEFAULT_LIFECYCLE_STATE_FILTER,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';
import { useArchivedLifecycleFilter } from '../../../hooks/useArchivedLifecycleFilter';
import { useMainContentTabs } from '../../../store/layoutStore';
import { useAiComponentSignalCounts } from '../../../hooks/api/useAiComponentSignalCounts';
import { useAiComponentFindingCounts } from '../../../hooks/api/useAiComponentFindingCounts';
import { EmptyState } from '../../EmptyState';
import { SurfaceAssetsContextNavigation } from '../surface-assets/SurfaceAssetsContextNavigation';
import { AiComponentsFilterPanel } from './AiComponentsFilterPanel';
import { AiComponentsTable } from './AiComponentsTable';
import {
  AI_COMPONENTS_GROUP_BY_FIELDS,
  EMPTY_AI_COMPONENTS_FILTERS,
  applyFilteredAiComponentTypeChipCounts,
  buildAiComponentTabInput,
  buildAiComponentsListQuery,
  buildAiComponentsTypeFacetQuery,
  hasAnyAiComponentsFilters,
  resolveAiComponentQueryTypes,
  resolveAiComponentTypeChips,
  toAiComponentsSearchTerm,
  type AiComponentType,
  type AiComponentsFilters,
  type AiComponentsGroupByField,
  type AiComponentsSort,
} from './ai-components-table.model';
import {
  useAiComponentsFacet,
  useAiComponentsListData,
} from './useAiComponentsListData';
import { useAiComponentsTableRows } from './useAiComponentsTableRows';

type AiComponentsListProps = {
  applicationId: string;
  segment: AiComponentListSegment;
  isMcpApplication: boolean;
  header: ReactNode;
  tabs?: ReactNode;
};

export function AiComponentsList({
  applicationId,
  segment,
  isMcpApplication,
  header,
  tabs,
}: AiComponentsListProps) {
  const tAssets = useTranslations('aiApplications.assets');
  const tComponentTypes = useTranslations('aiApplications.componentTypes');
  const tAssetList = useTranslations('internalNetwork.assetList');
  const tCommon = useTranslations('common');
  const [selectedType, setSelectedType] = useState<AiComponentType | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebounceValue(searchTerm, 300);
  const [filters, setFilters] = useState<AiComponentsFilters>(
    EMPTY_AI_COMPONENTS_FILTERS,
  );
  const [lifecycleState, setLifecycleState] = useState<LifecycleStateFilterDto>(
    DEFAULT_LIFECYCLE_STATE_FILTER,
  );
  const [sort, setSort] = useState<AiComponentsSort>(null);
  const [groupBy, setGroupBy] = useState<AiComponentsGroupByField | null>(null);
  const { navigateInActiveTab } = useMainContentTabs();
  const { lifecycleQueryParam, showLifecycleSelector } =
    useArchivedLifecycleFilter({ lifecycleState });
  const query = buildAiComponentsListQuery({
    segment,
    isMcpApplication,
    selectedType,
    search: debouncedSearch,
    filters,
    sort,
    groupBy: groupBy ?? undefined,
    lifecycleState: lifecycleQueryParam,
  });
  const facetQuery = {
    segment,
    lifecycleState: lifecycleQueryParam,
    types: resolveAiComponentQueryTypes({
      selectedType: null,
      isMcpApplication,
    }),
  };
  const hasSearchOrFilters =
    hasAnyAiComponentsFilters(filters) ||
    toAiComponentsSearchTerm(debouncedSearch) !== undefined;
  const listData = useAiComponentsListData({ applicationId, query });
  const typeFacet = useAiComponentsFacet({
    applicationId,
    query: facetQuery,
    groupBy: 'type',
  });
  const filteredTypeFacet = useAiComponentsFacet({
    applicationId,
    query: buildAiComponentsTypeFacetQuery({
      segment,
      isMcpApplication,
      search: debouncedSearch,
      filters,
      lifecycleState: lifecycleQueryParam,
    }),
    groupBy: 'type',
    enabled: hasSearchOrFilters,
  });
  const roleFacet = useAiComponentsFacet({
    applicationId,
    query: facetQuery,
    groupBy: 'role',
    enabled: !isMcpApplication,
  });
  const signalCounts = useAiComponentSignalCounts({ applicationId });
  const findingCounts = useAiComponentFindingCounts({ applicationId });
  const detections = useMemo(
    () => ({
      signalCountsByComponentId: signalCounts.signalCountsByComponentId,
      findingCountsByComponentId: findingCounts.findingCountsByComponentId,
      isSignalsUnavailable: signalCounts.isError || signalCounts.isLoading,
      isFindingsUnavailable: findingCounts.isError || findingCounts.isLoading,
    }),
    [
      findingCounts.findingCountsByComponentId,
      findingCounts.isError,
      findingCounts.isLoading,
      signalCounts.signalCountsByComponentId,
      signalCounts.isError,
      signalCounts.isLoading,
    ],
  );
  const tableRows = useAiComponentsTableRows({
    components: listData.components,
    groupBy: groupBy ?? undefined,
    groups: listData.groups,
    resetKey: `${segment}:${selectedType ?? 'ALL'}:${groupBy ?? ''}`,
  });
  const unfilteredTypeChips = useMemo(
    () =>
      resolveAiComponentTypeChips({
        groups: typeFacet.groups,
        isMcpApplication,
      }),
    [isMcpApplication, typeFacet.groups],
  );
  const filteredTypeChips = useMemo(
    () =>
      hasSearchOrFilters
        ? resolveAiComponentTypeChips({
            groups: filteredTypeFacet.groups,
            isMcpApplication,
          })
        : null,
    [filteredTypeFacet.groups, hasSearchOrFilters, isMcpApplication],
  );
  const typeChips = useMemo(
    () =>
      applyFilteredAiComponentTypeChipCounts({
        chips: unfilteredTypeChips,
        filteredChips: filteredTypeChips,
      }).map((chip) => ({
        key: chip.key,
        type: chip.type,
        count: chip.count,
        label: chip.type
          ? tComponentTypes(AI_COMPONENT_TYPE_KEYS[chip.type])
          : tCommon('all'),
      })),
    [filteredTypeChips, tCommon, tComponentTypes, unfilteredTypeChips],
  );
  const getTypeCount = useCallback(
    (type: AiComponentType | null) =>
      typeChips.find((chip) => chip.type === type)?.count ?? 0,
    [typeChips],
  );
  const unfilteredSelectedTypeCount =
    unfilteredTypeChips.find((chip) => chip.type === selectedType)?.count ?? 0;
  const groupByOptions = useMemo(
    () =>
      AI_COMPONENTS_GROUP_BY_FIELDS.map((field) => ({
        value: field,
        label:
          field === 'type'
            ? tAssets('type')
            : field === 'role'
              ? tAssets('role')
              : tAssets('coverage'),
      })),
    [tAssets],
  );
  const handleSelectType = useCallback((type: AiComponentType | null) => {
    setSelectedType(type);
    setSort(null);
  }, []);
  const handleOpenComponent = useCallback(
    (component: AiComponentDto) => {
      navigateInActiveTab(buildAiComponentTabInput(component));
    },
    [navigateInActiveTab],
  );
  const clearFilters = useCallback(() => {
    setFilters(EMPTY_AI_COMPONENTS_FILTERS);
    setLifecycleState(DEFAULT_LIFECYCLE_STATE_FILTER);
  }, []);
  const hasActiveFilters =
    hasAnyAiComponentsFilters(filters) ||
    lifecycleState !== DEFAULT_LIFECYCLE_STATE_FILTER;
  const hasNarrowing =
    hasActiveFilters ||
    selectedType !== null ||
    toAiComponentsSearchTerm(debouncedSearch) !== undefined;
  const isEmpty =
    !listData.isLoading && !listData.isError && tableRows.rows.length === 0;

  return (
    <ListPageLayout
      header={header}
      tabs={tabs}
      navigation={
        <SurfaceAssetsContextNavigation
          items={typeChips}
          selectedKind={selectedType}
          onSelectKind={handleSelectType}
          getCount={getTypeCount}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          searchPlaceholder={tAssets('searchComponents')}
          onRefresh={listData.refresh}
          isLoading={listData.isLoading || listData.isRefetching}
          groupByOptions={groupByOptions}
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          areAllGroupsCollapsed={tableRows.areAllGroupsCollapsed}
          onExpandAllGroups={tableRows.expandAllGroups}
          onCollapseAllGroups={tableRows.collapseAllGroups}
        />
      }
      filters={
        isMcpApplication ? undefined : (
          <AiComponentsFilterPanel
            filters={filters}
            onFiltersChange={setFilters}
            roleGroups={roleFacet.groups}
            isLifecycleEnabled={showLifecycleSelector}
            lifecycleState={lifecycleState}
            onLifecycleStateChange={setLifecycleState}
            hasActiveFilters={hasActiveFilters}
            onClear={clearFilters}
            filteredCount={listData.totalCount}
            totalCount={unfilteredSelectedTypeCount}
          />
        )
      }
    >
      {listData.isError ? (
        <Box flex={1} minH={0}>
          <EmptyState
            icon={AlertCircleIcon}
            title={tAssetList('loadError')}
            description={tAssetList('loadErrorDescription')}
          />
        </Box>
      ) : isEmpty ? (
        <Box flex={1} minH={0}>
          <EmptyState
            icon={hasNarrowing ? Search01Icon : Database01Icon}
            title={hasNarrowing ? tAssetList('noMatching') : tAssets('empty')}
            description={
              hasNarrowing ? tAssetList('noMatchingDescription') : undefined
            }
          />
        </Box>
      ) : (
        <AiComponentsTable
          segment={segment}
          rows={tableRows.rows}
          loadMoreRowIndex={tableRows.loadMoreRowIndex}
          sort={sort}
          detections={detections}
          collapsedGroupKeys={tableRows.collapsedGroupKeys}
          isLoading={listData.isLoading}
          isFetchingMore={listData.isFetchingMore}
          hasMore={listData.hasMore}
          onLoadMore={listData.loadMore}
          onSortChange={setSort}
          onToggleGroup={tableRows.toggleGroup}
          onOpenComponent={handleOpenComponent}
        />
      )}
    </ListPageLayout>
  );
}
